import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  SectionHead,
  Card,
  ShopTheNetwork,
  buttonStyles,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";
import { PLATFORMS } from "../content/platforms";

const MCP_ENDPOINT = "https://mcp.comos-federation.com/mcp";

/**
 * AgentsPage — the agent-builder door, rebuilt on the design system. (CO 069)
 * Threads to /demo (try it live), /coms (the currency agents spend), /trust.
 */
export function AgentsPage() {
  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              For agent builders
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5.5vw,3.75rem)] leading-[1.08] text-balance"
            >
              One endpoint. The whole commerce control plane.
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              Catalog, cart, checkout, orders, inventory, email, shipping,
              payments — every ComOS tenant exposes the same MCP shape. Hook in
              once, ship across the network.
            </motion.p>
            <motion.div
              variants={fadeUp}
              className="mt-8 flex flex-col sm:flex-row gap-3 justify-center"
            >
              <Link to="/demo" className={buttonStyles({ variant: "primary" })}>
                Try it live →
              </Link>
              <Link to="/docs" className={buttonStyles({ variant: "outline" })}>
                Read the spec
              </Link>
            </motion.div>
          </motion.div>
        </Container>
      </Section>

      {/* The endpoint */}
      <Section tint="sunk">
        <Container>
          <motion.div
            {...inView}
            variants={fadeUp}
            className="max-w-3xl mx-auto"
          >
            <Card className="bg-ink text-paper dark:bg-black font-mono text-sm overflow-x-auto">
              <div className="flex items-center justify-between mb-3 gap-3">
                <span className="text-eyebrow uppercase text-signal-bright font-grotesk">
                  MCP endpoint
                </span>
                <span className="text-xs text-flow-bright">
                  Streamable HTTP · OAuth 2.1 · multi-tenant
                </span>
              </div>
              <p className="text-paper/90 break-all mb-4">{MCP_ENDPOINT}</p>
              <p className="text-mute-soft"># Discover what a tenant exposes</p>
              <p>
                <span className="text-signal-bright">
                  federation_list_tenants
                </span>
                ()
              </p>
              <p>
                <span className="text-signal-bright">
                  federation_tenant_info
                </span>
                ({"{ tenantId: "}
                <span className="text-flow-bright">"acme-audio"</span>
                {" }"})
              </p>
              <p className="mt-3 text-mute-soft">
                # Shop, add, check out
              </p>
              <p>
                <span className="text-signal-bright">catalog_search</span>(
                {"{ query: "}
                <span className="text-flow-bright">"wireless headphones"</span>
                {" }"})
              </p>
              <p>
                <span className="text-signal-bright">order_create</span>(
                {"{ items }"}){" "}
                <span className="text-mute-soft">
                  # reserve → price → settle, one atomic call
                </span>
              </p>
            </Card>
          </motion.div>
          <motion.div
            {...inView}
            variants={fadeUp}
            className="mt-10 max-w-4xl mx-auto"
          >
            <Card>
              <ShopTheNetwork />
            </Card>
          </motion.div>
        </Container>
      </Section>

      {/* Every platform agent-callable */}
      <Section>
        <Container>
          <SectionHead
            eyebrow="Coverage"
            title="Every platform, agent-callable"
            lead="Not just catalog. The whole OS — including the platforms the legacy stack hides behind dashboards."
          />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-10 grid gap-4 md:grid-cols-2"
          >
            {PLATFORMS.filter((p) => p.agentCall).map((p) => (
              <motion.div key={p.slug} variants={fadeUp}>
                <Card className="h-full">
                  <h3 className="text-display-sm mb-2">{p.name}</h3>
                  <code className="block rounded-lg bg-mute-surface dark:bg-ink-line text-signal dark:text-signal-bright font-mono text-sm px-3 py-2 break-words">
                    {p.agentCall}
                  </code>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* Protocols */}
      <Section tint="sunk">
        <Container>
          <SectionHead
            eyebrow="Why agent-native matters"
            title="Built for the shift"
          />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-10 grid gap-5 md:grid-cols-3"
          >
            {[
              {
                label: "MCP",
                title: "Model Context Protocol",
                body: "Every ComOS verb exposed as an MCP tool, with multi-tenant routing built in.",
              },
              {
                label: "UCP",
                title: "Universal Commerce Protocol",
                body: "Catalog, orders, and OAuth served live at /.well-known/ucp — the discovery surface the Google/Shopify agent stack reads.",
              },
              {
                label: "REST",
                title: "OpenAPI / REST",
                body: "The same tools over plain HTTPS, described at /openapi.json and /.well-known/openapi-actions.json for agents that call REST rather than MCP.",
              },
            ].map((p) => (
              <motion.div key={p.label} variants={fadeUp}>
                <Card className="h-full">
                  <span className="inline-flex items-center rounded-pill bg-signal px-2.5 py-1 text-xs font-grotesk font-semibold text-white mb-3">
                    {p.label}
                  </span>
                  <h3 className="text-display-sm mb-1.5">{p.title}</h3>
                  <p className="text-mute dark:text-mute-soft text-sm">
                    {p.body}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* Coms thread + close */}
      <Section tint="ink">
        <Container className="text-center">
          <motion.div
            {...inView}
            variants={fadeUp}
            className="max-w-2xl mx-auto"
          >
            <h2 className="text-display-lg text-balance text-paper">
              Agents act in Coms — a currency built to be spent.
            </h2>
            <p className="text-lead text-paper/70 mt-6">
              The agents you build spend the tenant's Coms under caps and
              autonomy they set. Use without custody, on a legible peg.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/coms"
                className={buttonStyles({ variant: "primary", size: "lg" })}
              >
                How Coms work
              </Link>
              <Link
                to="/start"
                className={buttonStyles({
                  variant: "outline",
                  size: "lg",
                  className:
                    "border-paper/25 text-paper hover:text-white hover:border-paper",
                })}
              >
                Get a tenant
              </Link>
            </div>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}
